/**
 * WebAddressService - Web platform implementation of IAddressService
 * Integrates with existing DeliveryService and browser APIs for address management
 */

import type {
  IAddressService,
  AddressSearchResult,
  DeliveryAddressData,
  AddressValidationResult,
  AddressDetailsFormData,
  AddressSummary
} from '../../contracts/IAddressService'
import { DeliveryService, PlacesService, geolocationService, useAuthStore, useRestaurantStore } from '@restaurant/core'
import type { DeliveryAddress } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Web-specific address service implementation
 * Uses PlacesService for address search and DeliveryService for backend integration
 */
export class WebAddressService implements IAddressService {
  private deliveryService: DeliveryService
  private placesService: PlacesService
  private readonly STORAGE_KEYS = {
    DELIVERY_ADDRESS: 'delivery_address',
    ADDRESS_DETAILS: 'address_details',
    RECENT_ADDRESSES: 'recent_addresses',
  }

  constructor(deliveryService: DeliveryService, placesService: PlacesService) {
    this.deliveryService = deliveryService
    this.placesService = placesService
  }

  async searchAddresses(query: string): Promise<AddressSearchResult[]> {
    try {
      if (!query || query.trim().length < 3) {
        return []
      }

      // Get cached location for biasing (non-blocking)
      const location = await geolocationService.getLocation()

      // Use PlacesService to fetch real address suggestions with location biasing
      const predictions = await this.placesService.fetchSuggestions(
        query,
        location ?? undefined
      )

      // Convert predictions to AddressSearchResult format
      // Note: We'll need to fetch details for each prediction to get full address
      const results: AddressSearchResult[] = []

      for (const prediction of predictions.slice(0, 5)) { // Limit to 5 results
        try {
          const placeDetails = await this.placesService.getPlaceDetails(prediction.placeId)

          if (placeDetails) {
            results.push({
              id: prediction.placeId,
              addressLine1: placeDetails.street,
              addressLine2: `${placeDetails.city}, ${placeDetails.state} ${placeDetails.zipCode}`,
              street: placeDetails.street,
              city: placeDetails.city,
              state: placeDetails.state,
              zipCode: placeDetails.zipCode,
              latitude: placeDetails.latitude,
              longitude: placeDetails.longitude,
              country: placeDetails.country || 'US',
            })
          }
        } catch (error) {
          const logger = safeGetLogger()
          logger?.error('Failed to fetch place details', { domain: 'delivery', error: String(error) })
          // Continue with other predictions
        }
      }

      return results
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Address search failed', { domain: 'delivery', error: String(error) })
      throw new Error('Unable to search addresses. Please try again.')
    }
  }

  async validateAddress(address: AddressSearchResult): Promise<AddressValidationResult> {
    try {
      // Convert AddressSearchResult to DeliveryAddress format
      const deliveryAddress: DeliveryAddress = {
        id: address.id,
        userId: '', // Will be set by service
        businessId: useRestaurantStore.getState().currentRestaurant?.id || '',
        street: address.street,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        latitude: address.latitude,
        longitude: address.longitude,
        lastUsed: new Date().toISOString(),
        storeDeliveryFees: {},
      }

      // Use existing DeliveryService for validation
      const validationResult = await this.deliveryService.validateDeliveryAddress(deliveryAddress)

      // Convert result format
      return {
        isValid: validationResult.isValid,
        isDeliverable: validationResult.isDeliverable,
        normalizedAddress: validationResult.normalizedAddress ?
          this.convertDeliveryAddressToSearchResult(validationResult.normalizedAddress) : null,
        suggestions: validationResult.suggestions ?
          validationResult.suggestions.map(addr => this.convertDeliveryAddressToSearchResult(addr)) : [],
        errors: validationResult.errors || [],
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Address validation failed', { domain: 'delivery', error: String(error) })
      throw new Error('Unable to validate address. Please try again.')
    }
  }

  async getDeliveryAddress(): Promise<AddressSummary | null> {
    try {
      // First check the Zustand delivery store (persisted as 'delivery-address-storage')
      const zustandStore = localStorage.getItem('delivery-address-storage')
      if (zustandStore) {
        const parsed = JSON.parse(zustandStore)
        const addr = parsed?.state?.currentAddress
        if (addr?.street) {
          return {
            addressLine1: addr.street,
            addressLine2: `${addr.city}, ${addr.state} ${addr.zipCode}`,
          }
        }
      }

      // Fallback to legacy key
      const storedAddress = localStorage.getItem(this.STORAGE_KEYS.DELIVERY_ADDRESS)
      if (storedAddress) {
        return JSON.parse(storedAddress)
      }
      return null
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get delivery address', { domain: 'delivery', error: String(error) })
      return null
    }
  }

  async updateDeliveryAddress(address: AddressSummary): Promise<void> {
    try {
      localStorage.setItem(this.STORAGE_KEYS.DELIVERY_ADDRESS, JSON.stringify(address))

      // Also store in recent addresses for quick access
      await this.addToRecentAddresses(address)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to update delivery address', { domain: 'delivery', error: String(error) })
      throw new Error('Unable to save delivery address. Please try again.')
    }
  }

  async saveAddressDetails(addressData: DeliveryAddressData): Promise<void> {
    try {
      const userId = useAuthStore.getState().user?.id || ''
      const businessId = useRestaurantStore.getState().currentRestaurant?.id || ''

      // Prepare address for API — include all backend-required fields
      const addressToSave = {
        userId,
        businessId,
        street: addressData.street,
        city: addressData.city,
        state: addressData.state,
        zipCode: addressData.zipCode,
        country: addressData.country || 'US',
        apartment: addressData.unitNumber,
        instructions: addressData.deliveryNotes,
        latitude: addressData.latitude,
        longitude: addressData.longitude,
        isDefault: false,
        deliveryDetails: {
          dropOffOption: addressData.deliveryPreference === 'hand_to_me' ? 'handItToMe' as const : 'leaveItAtMyDoor' as const,
          instructions: addressData.deliveryNotes || undefined,
        },
        storeDeliveryFees: {},
      }

      // Save address via API
      await this.deliveryService.saveUserAddress(addressToSave)

      // Store address details locally for quick access
      const detailsToStore: AddressDetailsFormData = {
        unitNumber: addressData.unitNumber,
        entryCode: addressData.entryCode,
        buildingName: addressData.buildingName,
        // Map the backend save-vocabulary to the form vocabulary ('door'|'hand').
        deliveryPreference: addressData.deliveryPreference === 'hand_to_me' ? 'hand' : 'door',
        deliveryNotes: addressData.deliveryNotes,
      }

      localStorage.setItem(this.STORAGE_KEYS.ADDRESS_DETAILS, JSON.stringify(detailsToStore))

      // Also save as delivery address summary
      const addressSummary: AddressSummary = {
        addressLine1: addressData.street,
        addressLine2: `${addressData.city}, ${addressData.state} ${addressData.zipCode}`,
        deliveryInstructions: addressData.deliveryNotes,
      }

      await this.updateDeliveryAddress(addressSummary)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to save address details', { domain: 'delivery', error: String(error) })
      throw new Error('Unable to save address details. Please try again.')
    }
  }

  async getAddressDetails(): Promise<AddressDetailsFormData> {
    try {
      const storedDetails = localStorage.getItem(this.STORAGE_KEYS.ADDRESS_DETAILS)
      if (storedDetails) {
        return JSON.parse(storedDetails)
      }

      // Return default values
      return {
        unitNumber: '',
        entryCode: '',
        buildingName: '',
        deliveryPreference: 'door',
        deliveryNotes: '',
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get address details', { domain: 'delivery', error: String(error) })
      return {
        unitNumber: '',
        entryCode: '',
        buildingName: '',
        deliveryPreference: 'door',
        deliveryNotes: '',
      }
    }
  }

  // Private helper methods

  private convertDeliveryAddressToSearchResult(deliveryAddress: DeliveryAddress): AddressSearchResult {
    return {
      id: deliveryAddress.id,
      addressLine1: deliveryAddress.street,
      addressLine2: `${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zipCode}`,
      street: deliveryAddress.street,
      city: deliveryAddress.city,
      state: deliveryAddress.state,
      zipCode: deliveryAddress.zipCode,
      latitude: deliveryAddress.latitude,
      longitude: deliveryAddress.longitude,
      country: 'US',
    }
  }

  private async addToRecentAddresses(address: AddressSummary): Promise<void> {
    try {
      const recentAddressesJson = localStorage.getItem(this.STORAGE_KEYS.RECENT_ADDRESSES)
      let recentAddresses: AddressSummary[] = recentAddressesJson ? JSON.parse(recentAddressesJson) : []

      // Remove duplicate if exists
      recentAddresses = recentAddresses.filter(
        addr => addr.addressLine1 !== address.addressLine1 || addr.addressLine2 !== address.addressLine2
      )

      // Add to front
      recentAddresses.unshift(address)

      // Keep only last 5 addresses
      recentAddresses = recentAddresses.slice(0, 5)

      localStorage.setItem(this.STORAGE_KEYS.RECENT_ADDRESSES, JSON.stringify(recentAddresses))
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to update recent addresses', { domain: 'delivery', error: String(error) })
      // Non-critical, don't throw
    }
  }
}
